import React from 'react';
import { DayPilot } from "daypilot-pro-react";
import Section from '../../common/Section';
import SectionHeader from '../../common/SectionHeader';
import Zoom from '../../calendar/Zoom';
import Navigation from '../../calendar/Navigation';
import Calendar from '../../calendar';
import TaskService from '../../../services/TaskService';
import TeamDropdown from './TeamDropdown';
import SelectInput from '../../form/controls/SelectInput';
import UserService from '../../../services/UserService';

import '../../calendar/styles.css';
import './calendarview.css';

class CalendarView extends React.Component {

  constructor(props) {
    super(props);

    this.periodChange = this.periodChange.bind(this);
    this.zoomChange = this.zoomChange.bind(this);
    this.onSelectTeam = this.onSelectTeam.bind(this);
    this.onSelectUser = this.onSelectUser.bind(this);
    this.onBeforeEventRender = this.onBeforeEventRender.bind(this);
    this.onEventClick = this.onEventClick.bind(this);

    this.state = {
      locale: "en-us",
      startDate: DayPilot.Date.today(),
      viewType: "Month",
      timeRangeSelectedHandling: "Disabled",
      eventMoveHandling: "Disabled",
      eventResizeHandling: "Disabled",
      eventDeleteHandling: "Disabled",
      eventClickHandling: "Disabled",
      eventHoverHandling: "Bubble",
      showWeekend: true,
      showAllDayEvents: true,
      businessBeginsHour: 8,
      businessEndsHour: 20,
      eventHeight: 55,
      cellDuration: 30,
      events: [],
      users: [],
      selectedUsers: [],
      selectedTeams: [],
      currentUser: null
    };
  }

  static getDerivedStateFromProps(props, state) {

    if (state.currentUser == null || props.user.id !== state.currentUser.id) {

      const { id, firstName, lastName, team, teamLead, projectRole } = props.user;
      const user = { id, name: `${firstName} ${lastName}` };
      const isObserver = projectRole.id === 9;

      return {
        selectedTeams: isObserver ? [] : [team],
        selectedUsers: isObserver ? [] : [user],
        currentUser: { teamLead, team, isObserver, ...user }
      }
    }

    return null;
  }

  componentDidMount() {
    this.loadUsers(this.state.selectedTeams)
      .then(users => this.setState({ users }, this.loadEvents));
  }

  loadEvents() {

    let start = this.state.startDate.firstDayOfMonth().toString();
    let end = this.state.startDate.addMonths(1).firstDayOfMonth().toString();

    switch (this.state.viewType.toLowerCase()) {
      case "day":
        start = this.state.startDate.toString();
        end = this.state.startDate.addDays(1).toString();
        break;
      case "week":
        start = this.state.startDate.firstDayOfWeek().toString();
        end = this.state.startDate.addDays(7).toString();
        break;
      default:
        break;
    }

    const requestParams = {
      start: start,
      end: end,
      users: this.state.selectedUsers.map(user => user.id),
      teams: this.state.selectedTeams.map(team => team.id)
    };

    TaskService.getTasks(requestParams)
      .then(result => {

        const events = result.items.reduce((list, task) => {
          list.push(this.parseTaskResponse(task));
          return list;
        }, []);

        return events;
      })
      .then(response => {
        return this.setState({
          events: [...response]
        });
      });
  }

  parseTaskResponse(taskResource) {

    const task = { id: taskResource.id, ...taskResource.attributes };

    return {
      id: task.id,
      start: new DayPilot.Date(task.startTime),
      end: new DayPilot.Date(task.endTime),
      title: task.title,
      text: task.description,
      resource: task.user ? task.user.id : -999,
      resourceName: task.user ? task.user.name : 'Unassigned',
      categoryId: task.catagoryId,
      allDay: task.allDay,
      completed: task.completed,
      projectId: task.projectId,
      moveVDisabled: false,
      moveHDisabled: true
    }
  }

  updateEvents(startDate) {
    if (this.state.startDate !== startDate) {
      this.setState({ startDate: startDate }, this.loadEvents);
    }
  }

  loadUsers(teams) {

    const params = {
      teams: teams.map(team => team.id) || [],
      page: { size: 100 }
    };

    return UserService
      .get(params)
      .then(data => {
        let result = data.items.map((user) => {
          return {
            id: user.id,
            name: `${user.attributes.firstName} ${user.attributes.lastName}`
          };
        });
        return result || [];
      });
  }

  onSelectTeam(value) {

    this.loadUsers(value)
      .then(result => {

        let intersection = this.state.selectedUsers;
        if (value.length && intersection.length) {
          intersection = intersection.filter(x => result.some(u => x.id === u.id));
        }

        this.setState({ selectedTeams: value, selectedUsers: intersection, users: [...result] }, this.loadEvents);
      });
  }

  onSelectUser(name, value) {
    this.setState({ selectedUsers: value }, this.loadEvents);
  }

  periodChange(args) {

    const direction = Number(args.direction);
    let newStart = this.state.startDate;

    switch (this.state.viewType.toLowerCase()) {
      case "day":
        newStart = this.state.startDate.addDays(direction);
        break;
      case "week":
        newStart = this.state.startDate.addDays(7 * direction);
        break;
      case "month":
        newStart = this.state.startDate.addMonths(direction);
        break;
      default:
        break;
    }

    this.updateEvents(newStart);
  }

  zoomChange(args) {
    this.setState({ viewType: args.level }, this.loadEvents);
  }

  onBeforeEventRender(args) {

    const startTime = args.e.start.toString('hh:mm');
    const endTime = args.e.end.toString('hh:mm');
    const timeRange = args.e.allDay ? "All day" : `${startTime}-${endTime}`;

    args.e.html = `<strong>${args.e.text}</strong><br />${args.e.resourceName}<br />${timeRange}`;
    args.e.bubbleHtml = args.e.html;
  }

  onEventClick(args) {

    if (this.props.onViewDetails) {
      this.props.onViewDetails(args.e.data.projectId);
    }

    args.preventDefault();
  }

  render() {

    const isObserver = this.state.currentUser.isObserver;
    const isFixedUser = !isObserver && !this.state.currentUser.teamLead;

    return (
      <React.Fragment>
        <SectionHeader title="My schedule">
          <div className="filter">
            <div className="filter-dropdown">
              <TeamDropdown
                name="teams"
                placeholder="Select teams to filter users"
                multi={true}
                selected={this.state.selectedTeams}
                disabled={!isObserver}
                onChange={this.onSelectTeam} />
            </div>
            <div className="filter-dropdown">
              <SelectInput
                name="users"
                placeholder="Select user to filter events"
                multi={true}
                options={this.state.users}
                value={this.state.selectedUsers}
                disabled={isFixedUser}
                onChange={this.onSelectUser} />
            </div>
          </div>
        </SectionHeader>
        <Section className="edit-page summary">
          <div className="calendar-container">
            <div className="header">
              <Zoom
                allowMonth={true}
                selected={this.state.viewType}
                onChange={this.zoomChange} />
              <div className="title">{this.state.startDate.toString("MMMM, yyyy")}</div>
              <Navigation
                onChange={this.periodChange} />
            </div>
            <Calendar
              {...this.state}
              onBeforeEventRender={this.onBeforeEventRender}
              onEventClick={this.onEventClick} />
          </div>
        </Section>
      </React.Fragment>
    );
  }
}

export default CalendarView;



// WEBPACK FOOTER //
// ./src/components/projects/ScheduledProjects/CalendarView.js